import React from 'react';
import { GraduationCap, Search, Map, BookOpen, Lightbulb, Crown, Star, Trophy, MessageCircle, HelpCircle, Users } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useMembershipAccess } from '@/hooks/useMembershipAccess';

const CollegeSearchSidebar: React.FC = () => {
  const navigate = useNavigate();
  const { hasAccessTo } = useMembershipAccess();
  const isPremium = hasAccessTo('college-search');

  return (
    <div className="space-y-4 sticky top-4">
      {/* Main Info Card */}
      <Card className="bg-gradient-to-b from-primary/5 to-secondary/5 border-primary/20">
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary/10 rounded-full">
              <GraduationCap className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-lg font-bold text-primary">College Directory</h2>
          </div>
          
          <p className="text-sm text-muted-foreground mb-4">
            Research thousands of colleges and universities to find your perfect athletic and academic fit.
          </p>
          
          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <Search className="h-4 w-4 text-secondary mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-sm">Search & Filter</p>
                <p className="text-xs text-muted-foreground">Filter by division, state, GPA, SAT/ACT, tuition, and more</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Map className="h-4 w-4 text-secondary mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-sm">Map View</p>
                <p className="text-xs text-muted-foreground">Explore colleges geographically</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <BookOpen className="h-4 w-4 text-secondary mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-sm">Detailed Research</p>
                <p className="text-xs text-muted-foreground">Academic requirements, athletic programs, and financial info</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Lightbulb className="h-4 w-4 text-secondary mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-sm">Personalized Suggestions</p>
                <p className="text-xs text-muted-foreground">AI-powered college matches based on your profile</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Premium Features Card */}
      <Card className="bg-secondary/5 border-secondary/20">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Crown className="h-5 w-5 text-secondary" />
            <span className="font-semibold text-secondary text-sm">Premium Features</span>
          </div>
          
          {!isPremium && (
            <Badge variant="secondary" className="text-xs mb-3">Upgrade to unlock</Badge>
          )}
          
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <Star className="h-4 w-4 text-secondary mt-0.5 shrink-0" />
              <span><strong>Save to Tracker:</strong> Add colleges to your Recruiting Tracker</span>
            </li>
            <li className="flex items-start gap-2">
              <Trophy className="h-4 w-4 text-secondary mt-0.5 shrink-0" />
              <span><strong>Organize Lists:</strong> Categorize as Dream, Target, or Safety</span>
            </li>
            <li className="flex items-start gap-2">
              <BookOpen className="h-4 w-4 text-secondary mt-0.5 shrink-0" />
              <span><strong>Track Progress:</strong> Log coach contacts, visits, and notes</span>
            </li>
            <li className="flex items-start gap-2">
              <Lightbulb className="h-4 w-4 text-secondary mt-0.5 shrink-0" />
              <span><strong>Smart Matches:</strong> Personalized college suggestions</span>
            </li>
          </ul>

          {!isPremium && (
            <Button 
              onClick={() => navigate('/athlete-dashboard/membership')}
              className="w-full mt-4"
              size="sm"
            >
              Upgrade Now
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Help & Resources Card */}
      <Card className="bg-muted/30 border-muted">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <HelpCircle className="h-5 w-5 text-primary" />
            <span className="font-semibold text-primary text-sm">Help & Resources</span>
          </div>
          
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <MessageCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span><strong>Contact Coaches:</strong> Tips for reaching out effectively</span>
            </li>
            <li className="flex items-start gap-2">
              <Users className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span><strong>Campus Visits:</strong> Plan your official and unofficial visits</span>
            </li>
            <li className="flex items-start gap-2">
              <GraduationCap className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span><strong>NCAA Eligibility:</strong> Understand academic requirements</span>
            </li>
          </ul>

          <Button 
            variant="outline"
            onClick={() => navigate('/knowledge-center')}
            className="w-full mt-4"
            size="sm"
          >
            View Knowledge Center
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default CollegeSearchSidebar;
